<template>
  <div>
    <service-screen-layout :parts="parts">
      <template v-slot:service>
        <div class="row">
          <div class="col-md-9">
            <div class="form-group row">
              <label class="col-md-3 col-form-label">Customer Name:</label>
              <div class="col-md-5">
                <input
                  type="text"
                  class="form-control"
                  v-model="saleDefinition.definition.mimecastmail.description"
                  :disabled="!isAuthorizedUser"
                  placeholder="Description"
                />
              </div>
            </div>
            <div class="form-group row">
              <label class="col-md-3 col-form-label">Domain:</label>
              <div class="col-md-5">
                <input
                  type="text"
                  class="form-control"
                  v-model="saleDefinition.definition.mimecastmail.domain"
                  readonly
                  placeholder="Name"
                />
              </div>
            </div>
            <div class="form-group row">
              <label class="col-md-3 col-form-label">Start Date:</label>
              <div class="col-md-5">
                <div class="input-group">
                  <DateInput 
                      type="date" 
                      :disabled="!isStaffUserType && productAccessRestricted"
                      v-model="saleDefinition.definition.mimecastmail.startDate"
                      style="width: 100%"
                  />
                  <div class="input-group-append">
                      <span class="input-group-text"><i class="fa fa-calendar" aria-hidden="true"></i></span>
                  </div>
                </div>
              </div>
            </div>
            <div class="form-group row">
              <label class="col-md-3 col-form-label">End Date:</label>
              <div class="col-md-5">
                <div class="input-group">
                  <DateInput 
                      type="date" 
                      :disabled="!isStaffUserType && productAccessRestricted"
                      v-model="saleDefinition.definition.mimecastmail.endDate"
                      style="width: 100%"
                  />
                  <div class="input-group-append">
                      <span class="input-group-text"><i class="fa fa-calendar" aria-hidden="true"></i></span>
                  </div>
                </div>
              </div>
            </div>
            <div class="row" v-allowed:view="['STAFF']">
              <div class="col-md-12">
                <div class="form-group">
                  <button
                    class="btn btn-success"
                    @click="saveSale()"
                    type="button"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:skus>
        <div class="form-group row">
          <div class="col-md-3">
            <button class="btn btn-success" type="button" @click="addSku()">Add SKU</button>  
          </div>
        </div>
        <div class="row row-spacing">
          <div class="col-md-12">
            <table
              class="table table-bordered"
              v-if="
                saleDefinition.definition.mimecastmail.mimecastskus.length > 0
              "
            >
              <thead>
                <tr>
                  <th>Description</th>
                  <th>SKU</th>
                  <th style="width: 10%">Markup</th>
                  <th style="width: 10%">Status</th>
                  <th style="width: 15%"></th>
                </tr>
              </thead>
              <tbody v-if="saleDefinition.definition.mimecastmail.mimecastskus.length > 0">
                <tr
                  v-for="item in saleDefinition.definition.mimecastmail.mimecastskus"
                  :key="item.mimecastSkuNumber"
                >
                  <td>{{ item.description }}</td>
                  <td>{{ item.sku }}</td>
                  <td class="text-center">{{ item.markup }}%</td>
                  <td class="text-center">
                    <span
                      class="badge"
                      :class="item.status ? 'badge-green' : 'badge-warning'"
                      >{{ item.status ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td class="text-center">
                    <button
                      v-tooltip="'Edit SKU'"
                      type="button"
                      @click="editSku(item)"
                      class="btn btn-sm btn-info m-1"
                    >
                      <em class="fa fa-edit fa-fw"></em>
                    </button>
                    <button
                      v-tooltip="'Delete SKU'"
                      type="button"
                      @click="removeSku(item)"
                      class="btn btn-sm btn-primary"
                    >
                      <em class="fa fa-trash fa-fw"></em>
                    </button>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td class="text-center" colspan="6">No items to display</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="alert alert-info">
              No SKUs loaded for
              {{ saleDefinition.definition.mimecastmail.domain }}
            </div>
          </div>
        </div>
      </template>
      <template v-slot:items>
        <div class="form-group row">
          <label class="col-md-2 col-form-label text-bold"
            >Service Month:</label
          >
          <div class="col-md-3">
            <v-select
              :options="periods"
              placeholder="Select Period"
              v-model="selectedPeriod"
            ></v-select>
          </div>
        </div>
        <div class="row row-spacing">
          <div class="col-md-12">
            <table
              class="table table-bordered"
              v-if="
                saleDefinition.definition.mimecastmail.mimecastitems.length > 0
              "
            >
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Item</th>
                  <th>SKU</th>
                  <th>Quantity</th>
                  <th v-if="isStaffUserType">Cost</th>
                  <th v-if="isStaffUserType">Price (with markup)</th>
                  <th v-else>Price</th>
                  <th v-if="isStaffUserType">Total (with markup)</th>
                  <th v-else>Total</th>
                </tr>
              </thead>
              <tbody v-if="filteredItems.length > 0">
                <tr
                  v-for="item in filteredItems"
                  :key="item.mimecastItemNumber"
                >
                  <td>{{ item.period }}</td>
                  <td>{{ item.item }}</td>
                  <td>{{ item.sku }}</td>
                  <td>{{ item.quantity }}</td>
                  <td v-if="isStaffUserType">{{ currencyFormat(item.unitCostExVat) }}</td>
                  <td>{{ currencyFormat(item.unitPriceExVat) }}</td>
                  <td>{{ currencyFormat(item.totalPriceExVat) }}</td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td class="text-center" colspan="6">No items to display</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="alert alert-info">
              No Items loaded for
              {{ saleDefinition.definition.mimecastmail.domain }}
            </div>
          </div>
        </div>
      </template>
    </service-screen-layout>

    <SkuModal
      ref="skuModal" 
      :modalTitle="selectedSku.mimecastSkuNumber ? 'Edit SKU' : 'Add SKU'"
      :selectedSku="selectedSku"
      @saveSku="saveSku"
    /> 
  </div>
</template>

<script>
import ServiceScreenLayout from "@/layouts/ServiceScreenLayout";
import SaleFunctions from "@/pages/Services/SaleFunctions";
import DateInput from "@/components/GeneralComponents/InputControls/DateInput";
import SkuModal from '@/components/Admin/Modals/SkuModal';

export default {
  extends: SaleFunctions,
  components: {
    ServiceScreenLayout,
    DateInput,
    SkuModal
  },
  data() {
    return {
      parts: [
        {
          name: "service",
          icon: "fa fa-info-circle",
          displayName: "Mimecast Service",
          permissions: ["*"],
        },
        {
          name: "skus",
          icon: "fa fa-cubes",
          displayName: "SKU Management",
          permissions: ["*"],
        },
        {
          name: "items",
          icon: "fa fa-calendar",
          displayName: "Service Items",
          permissions: ["*"],
        },
      ],
      saleDefinition: {
        definition: {
          mimecastmail: {
            mimecastitems: [],
            mimecastskus: [],
          },
        },
      },

      saleNumber: null,
      newSku: {},
      selectedSku: {},

      periods: [],
      period: 0,
      selectedPeriod: 0,
      productAccessRestricted: null,
      readOnly: {},
      fullAccess: {},
    };
  },
  mounted() {
    this.loadSaleDefinition();
    this.getProductRestrictions();
  },
  computed: {
    filteredItems() {
      return this.saleDefinition.definition.mimecastmail.mimecastitems.filter(
        (i) => i.period == this.selectedPeriod
      );
    },
    isAuthorizedUser() {
      return this.userContainsPermission(['ADMIN','PRODUCT_MANAGER']);
    },
    isSeniorAccounts() {
      return this.userContainsPermission(['ACCOUNTS_ADMIN']);
    },
    isStaffUserType() {
      return this.userContainsPermission(['STAFF']);
    },
  },
  methods: {
    saveSale() {
      this.$http
        .put(
          this.$config.aimsAPI +
            "sales/definition/" +
            this.saleDefinition.sale.saleNumber +
            "/update",
          this.saleDefinition
        )
        .then(
          () => {
            this.showSuccess("Save Success");
            this.loadSaleDefinition();
          },
          (error) => {
            console.error(error);
            this.showError("Error Saving SaleDefinition", error);
          }
        );
    },
    loadSaleDefinition() {
      const saleNumber = this.$route.params.saleNumber;
      this.getSaleDefinition(saleNumber).then(
        (response) => {
          this.saleDefinition = response.data;
          this.productAccessRestricted = this.saleDefinition.product.clientAccessRestriction === this.readOnly.description ? true : false;
          
          this.getItemPeriods();
          
        },
        (error) => {
          this.showError("Could not load Sale Definition", error);
          console.error(error);
        }
      );
    },
    getProductRestrictions: function() {
      const hostUrl = this.$config.aimsAPI;

      this.$http.get(`${hostUrl}products/restrictions`).then(
        (response) => {
          const accessRestrictions = response.data;
          this.readOnly = accessRestrictions.find((access) => access.name === 'CLIENT_READONLY');
          this.fullAccess = accessRestrictions.find((access) => access.name === 'CLIENT_USAGE');          
        },
        (error) => {
          console.error(error);
          this.showError("Error fetching product restrictions", error)
        }
      )
    },
    getItemPeriods() {
      this.$http
        .get(
          this.$config.aimsAPI +
            "mimecastservice/" +
            this.saleDefinition.definition.mimecastmail.mimecastServiceNumber +
            "/periods/"
        )
        .then(
          (response) => {
            this.periods = response.data;
          },
          (error) => {
            this.showError("Could not fetch Mimecast Item Periods", error);
            console.error(error);
          }
        );
    },

    addSku: function() {
      this.selectedSku = this.saleDefinition.supportingObjs.mimecastskus;
      this.selectedSku.mimecastServiceNumber = this.saleDefinition.definition.mimecastmail.mimecastServiceNumber;
      this.$refs.skuModal.open();
    },
    editSku: function(data) {
      this.selectedSku = data;
      this.$refs.skuModal.open();
    },
    saveSku: function(sku) {
      const hostUrl = this.$config.aimsAPI;
      const saleNumber = this.saleDefinition.sale.saleNumber;

      if (sku.mimecastServiceNumber) {
        this.$http.put(`${hostUrl}sales/definition/${saleNumber}/obj/update`, sku).then(
          (response) => {
            this.showSuccess("SKU Updated successfully");
            this.loadSaleDefinition();
          },
          (error) => {
            console.error(error);
            this.showError("Error Updating SPLA", error);
          }
        );
      }
    },
    removeSku: function(data) {
      this.$swal({
          title: "Are you sure you want to delete this SKU?",
          text: data.description,
          type: "warning",
          showCancelButton: true,
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes",
      }).then((result) => {
          if (result.value) {
            const hostUrl = this.$config.aimsAPI;
            const saleNumber = this.saleDefinition.sale.saleNumber;

            this.$http.post(`${hostUrl}sales/definition/${saleNumber}/obj/delete/`, data).then(
              (response) => {
                this.showSuccess("Delete Success");
                this.loadSaleDefinition();
              },(error) => {
                console.error(error);
                this.showError("Error Removing SKU", error);
              });
          }
      });
    },
  }
};
</script>